import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react'
import { clsx } from 'clsx'
import { motion } from 'framer-motion'
import { Menu as MenuIcon } from 'lucide-react'
import { Button } from './Button'
import { Link } from './Link'
import { Logo } from './Logo'
import { PlusGrid, PlusGridItem, PlusGridRow } from './PlusGrid'

// Decision 7 — single shared nav: 5 primary items (Home = logo) + 2 CTAs.
const links = [
  { href: '/how-it-works', label: 'How It Works' },
  { href: '/security', label: 'Security' },
  { href: '/platform', label: 'Platform' },
  { href: '/pricing', label: 'Pricing' },
]

function DesktopNav() {
  return (
    <nav className="relative hidden items-center lg:flex">
      {links.map(({ href, label }) => (
        <PlusGridItem key={href} className="relative flex">
          <Link
            href={href}
            className="flex items-center px-4 py-3 text-base font-medium text-gray-950 transition hover:bg-black/5"
          >
            <span className="nav-underline">{label}</span>
          </Link>
        </PlusGridItem>
      ))}
      <PlusGridItem className="relative flex">
        <Link
          href="/login"
          className="flex items-center px-4 py-3 text-base font-medium text-gray-950 transition hover:bg-black/5"
        >
          <span className="nav-underline">Sign In</span>
        </Link>
      </PlusGridItem>
      <div className="flex items-center pl-2">
        <Button href="/login">Start Free Trial</Button>
      </div>
    </nav>
  )
}

function MobileNavButton() {
  return (
    <DisclosureButton
      className="flex size-12 items-center justify-center self-center rounded-lg transition hover:bg-black/5 lg:hidden"
      aria-label="Open main menu"
    >
      <MenuIcon className="size-6" />
    </DisclosureButton>
  )
}

function MobileNav() {
  const mobileLinks = [...links, { href: '/login', label: 'Sign In' }]
  return (
    <DisclosurePanel className="lg:hidden">
      <div className="flex flex-col gap-6 py-4">
        {mobileLinks.map(({ href, label }, linkIndex) => (
          <motion.div
            initial={{ opacity: 0, rotateX: -90 }}
            animate={{ opacity: 1, rotateX: 0 }}
            transition={{
              duration: 0.15,
              ease: 'easeInOut',
              rotateX: { duration: 0.3, delay: linkIndex * 0.1 },
            }}
            key={href}
          >
            <Link href={href} className="text-base font-medium text-gray-950">
              {label}
            </Link>
          </motion.div>
        ))}
        <Button href="/login" className="w-full">
          Start Free Trial
        </Button>
      </div>
      <div className="absolute left-1/2 w-screen -translate-x-1/2">
        <div className="absolute inset-x-0 top-0 border-t border-black/5" />
        <div className="absolute inset-x-0 top-2 border-t border-black/5" />
      </div>
    </DisclosurePanel>
  )
}

export function Navbar({ banner }: { banner?: React.ReactNode }) {
  return (
    <Disclosure as="header" className={clsx('pt-12 sm:pt-16')}>
      {/* Keyboard users can bypass the nav (Gate 4 accessibility / M7). */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:rounded-full focus:bg-gray-950 focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-white"
      >
        Skip to content
      </a>
      <PlusGrid>
        <PlusGridRow className="relative flex justify-between">
          <div className="relative flex gap-6">
            <PlusGridItem className="py-3">
              <Link href="/" title="Home">
                <Logo className="h-9" />
              </Link>
            </PlusGridItem>
            {banner && (
              <div className="relative hidden items-center py-3 lg:flex">
                {banner}
              </div>
            )}
          </div>
          <DesktopNav />
          <MobileNavButton />
        </PlusGridRow>
      </PlusGrid>
      <MobileNav />
    </Disclosure>
  )
}
